import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Button,
    Input,
    Tag,
    Space,
    Row,
    Col,
    Upload,
    Divider,
    Breadcrumb,
    Select
} from 'antd';
import {
    PlusOutlined,
    UploadOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { setUserList } from "redux/actions";
import { routeToKeyMap, STATUS_CODE, AwardList } from "common/Constants";
import {
    getUserRoleId,
    getSelectedDepartmentId,
    getStatusListFromID,
    getUserInfo,
    getLeaveCategoryList,
    getClientid,
    getUserList,
    getRoleList,
} from "redux/selectors";
import { bindActionCreators } from "redux";
import { connect, useSelector } from "react-redux";
import axios from 'axios';
import { CALL_API } from 'common/API';
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;


const CVEditor = ({ ClientId, userInfo, saveUserProfile, updateUserList }) => {
    function generateUniqueId() {
        const randomPart = Math.random().toString(36).substring(2, 11);
        const timestamp = Date.now();
        return randomPart + timestamp;
    }

    const [uniqueId, setUniqueId] = useState(generateUniqueId());

    // ─── Free-text input state for Expertise & Languages ─────────────────────
    const [newExpertise, setNewExpertise] = useState("");
    const [newLanguage, setNewLanguage] = useState("");
    // ─────────────────────────────────────────────────────────────────────────

    let userId = userInfo?.userid;
    let splitUserId = userId?.split("@");
    let userIdWithoutDomain = splitUserId[0];
    let updatedUserId = userIdWithoutDomain + uniqueId;

    const history = useHistory();
    const location = useLocation();
    const [educationFiles, setEducationFiles] = useState({});
    const [certificationFiles, setCertificationFiles] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    // ─── Add expertise by pressing Enter or clicking the + button ────────────
    const handleAddExpertise = () => {
        const value = newExpertise.trim();
        if (!value) return;
        if (!editData.expertise.includes(value)) {
            setEditData((prev) => ({
                ...prev,
                expertise: [...prev.expertise, value],
            }));
        }
        setNewExpertise("");
    };

    const handleExpertiseKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddExpertise();
        }
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Add language by pressing Enter or clicking the + button ─────────────
    const handleAddLanguage = () => {
        const value = newLanguage.trim();
        if (!value) return;
        if (!editData.languages.includes(value)) {
            setEditData((prev) => ({
                ...prev,
                languages: [...prev.languages, value],
            }));
        }
        setNewLanguage("");
    };

    const handleLanguageKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddLanguage();
        }
    };
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (location.state?.cvData) {
            setEditData({
                ...defaultCVData,
                ...location.state.cvData,
                education: location.state.cvData.education?.length
                    ? location.state.cvData.education
                    : defaultCVData.education,
                certifications: location.state.cvData.certifications?.length
                    ? location.state.cvData.certifications
                    : defaultCVData.certifications,
            });
        } else {
            history.push("/my-profile");
        }
    }, []);

    const defaultCVData = {
        background: "",
        expertise: [],
        languages: [],
        education: [
            {
                title: "",
                institute: "",
                from: "",
                to: "",
                examBody: "",
                awarded: ""
            }
        ],
        certifications: [
            {
                title: "",
                type: "",
                from: "",
                to: "",
                remark: ""
            }
        ]
    };

    const [editData, setEditData] = useState(defaultCVData);

    const expertiseSuggestions = [
        "IFRS Audit", "AUP Engagement", "Statutory Audits", "Compilation Engagement",
        "Other assurance engagements", "Tax advisory Services", "Tax Compliance",
        "Assurance Services", "Corporate Finance", "Transactions Advisory",
        "Restructuring & M&A", "Risk Management", "RAS", "Forensics Investigations",
        "Regulatory & Compliance", "Management Consultancy", "Strategy Consultancy",
        "HR Management Consultancy", "PM Consultancy", "Devt. Advisory",
        "Technology Advisory", "Cyber security", "Analytics & Insights"
    ];

    const languageSuggestions = [
        "English", "Swahili", "French", "Spanish", "Hindi", "Arabic", "Portuguese",
        "Hausa", "Yoruba", "Amharic", "Oromo", "Kinyarwanda", "Somali", "Kirundi",
        "Luganda", "Mandarin", "Chinese", "German"
    ];

    const toggleExpertise = (item) => {
        const current = editData.expertise;
        if (current.includes(item)) {
            setEditData({ ...editData, expertise: current.filter(e => e !== item) });
        } else {
            setEditData({ ...editData, expertise: [...current, item] });
        }
    };

    const toggleLanguage = (item) => {
        const current = editData.languages;
        if (current.includes(item)) {
            setEditData({ ...editData, languages: current.filter(l => l !== item) });
        } else {
            setEditData({ ...editData, languages: [...current, item] });
        }
    };

    const removeEducation = (index) => {
        const updatedEducation = [...editData.education];
        updatedEducation.splice(index, 1);
        setEditData({ ...editData, education: updatedEducation });
        setEducationFiles((prev) => {
            const updatedFiles = { ...prev };
            delete updatedFiles[index];
            return updatedFiles;
        });
    };

    useEffect(() => {
        if (!location.state?.cvData) {
            history.push("/my-profile");
            return;
        }

        const cv = location.state.cvData;

        if (cv.education?.length) {
            const educationFilesMap = {};
            cv.education.forEach((edu, index) => {
                if (edu.attachment?.length) {
                    educationFilesMap[index] = edu.attachment.map((doc, i) => ({
                        uid: doc.documentId || `edu-${index}-${i}`,
                        documentId: doc.documentId,
                        name: doc.name || doc.documentName || doc.documentPath?.split("/")?.pop() || "Document",
                        status: "done",
                    }));
                }
            });
            setEducationFiles(educationFilesMap);
        }

        if (cv.certifications?.length) {
            const certFilesMap = {};
            cv.certifications.forEach((cert, index) => {
                if (cert.attachment?.length) {
                    certFilesMap[index] = cert.attachment.map((doc, i) => ({
                        uid: doc.documentId || `cert-${index}-${i}`,
                        documentId: doc.documentId,
                        name: doc.name || doc.documentName || doc.documentPath?.split("/")?.pop() || "Document",
                        status: "done",
                    }));
                }
            });
            setCertificationFiles(certFilesMap);
        }

        const normalizeEducation = cv.education?.map((e) => ({
            ...e,
            from: e.from && dayjs(e.from, "YYYY-MM-DD", true).isValid()
                ? dayjs(e.from).format("YYYY-MM-DD") : "",
            to: e.to && dayjs(e.to, "YYYY-MM-DD", true).isValid()
                ? dayjs(e.to).format("YYYY-MM-DD") : "",
        }));

        const normalizeCertifications = cv.certifications?.map((c) => ({
            ...c,
            title: c.title || c.certificationname || "",
            from: c.from && dayjs(c.from, "YYYY-MM-DD", true).isValid()
                ? dayjs(c.from).format("YYYY-MM-DD") : "",
            to: c.to && dayjs(c.to, "YYYY-MM-DD", true).isValid()
                ? dayjs(c.to).format("YYYY-MM-DD") : "",
        }));

        setEditData({
            ...defaultCVData,
            ...cv,
            education: normalizeEducation?.length ? normalizeEducation : defaultCVData.education,
            certifications: normalizeCertifications?.length ? normalizeCertifications : defaultCVData.certifications,
        });
    }, []);

    const uploadFile = async (file) => {
        try {
            const base64 = await convertToBase64(file);
            const payload = {
                userId: userInfo?.userid,
                name: file.name,
                type: file.type,
                uniqueId: file.uid,
                content: base64,
                cv: true
            };
            await CALL_API(`upload-userdocuments/${ClientId}`, "post", payload);
        } catch (error) {
            console.error("Upload error:", error);
            message.error("File upload failed");
        }
    };

    const fetchMasterData = async (fetch_master_data) => {
        const { code, userList } = await CALL_API(`master-data/${ClientId}`, "post", { fetch_master_data });
        if (code === STATUS_CODE.SUCCESS) {
            if (fetch_master_data) {
                updateUserList(userList);
                message.success("CV updated successfully");
                setUpdateLoading(false);
                history.push("/my-profile", { activeTab: 1 });
            }
        } else {
            message.error("Failed to fetch master data");
        }
    };


    const convertToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const realFile = file.originFileObj || file;
            if (!(realFile instanceof Blob)) { resolve(null); return; }
            const reader = new FileReader();
            reader.readAsDataURL(realFile);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (error) => reject(error);
        });

    const handleUpdate = async () => {
        if (!ClientId) { console.warn("ClientId not available yet"); return; }
        setUpdateLoading(true);
        setUniqueId(generateUniqueId());
        const payload = {
            user_id: userInfo?.userid,
            backgroundExperience: editData.background,
            expertise: editData.expertise,
            languages: editData.languages,
            educationdetails: await Promise.all(
                editData.education.map(async (edu, index) => ({
                    institutename: edu.institute,
                    title: edu.title,
                    exambody: edu.examBody,
                    awarded: edu.awarded,
                    fromdate: edu.from,
                    todate: edu.to,
                    attachment: await Promise.all(
                        (educationFiles[index] || []).map(async (file) => {
                            const content = await convertToBase64(file);
                            return { name: file.name, uniqueId: file.uid };
                        })
                    )
                }))
            ),
            professionalcertifications: editData.certifications.map((cert, index) => ({
                certificationtype: cert.type || "Professional Certification",
                certificationname: cert.title,
                title: cert.title || cert.certificationname || "",
                remarks: cert.remark,
                fromdate: cert.from,
                todate: cert.to,
                attachment: Array.isArray(certificationFiles?.[index])
                    ? certificationFiles[index].map((file) => ({
                        name: file.name,
                        uniqueId: file.documentId || file.uid
                    }))
                    : []
            }))
        };

        try {
            await CALL_API(`add-users/${ClientId}?cv=true`, "patch", payload);
            fetchMasterData(true);
        } catch (error) {
            console.error("CV update failed", error);
            message.error("CV update failed");
        }
    };

    const handleCancel = () => history.push("/my-profile", { activeTab: 1 });

    const addEducation = () => {
        setEditData((prev) => ({
            ...prev,
            education: [...prev.education, { title: "", institute: "", from: "", to: "", examBody: "", awarded: "" }]
        }));
    };

    const addCertification = () => {
        setEditData((prev) => ({
            ...prev,
            certifications: [...prev.certifications, { title: "", type: "", from: "", to: "", remark: "" }]
        }));
    };

    const removeCertification = (index) => {
        const updated = [...editData.certifications];
        updated.splice(index, 1);
        setEditData({ ...editData, certifications: updated });
        setCertificationFiles((prev) => {
            const updatedFiles = { ...prev };
            delete updatedFiles[index];
            return updatedFiles;
        });
    };

    // ─── Shared tag input style ───────────────────────────────────────────────
    const tagInputContainerStyle = {
        marginBottom: 16,
        padding: "10px",
        border: "1px solid #d5d5d5",
        borderRadius: "2px",
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        alignItems: "center",
    };

    const addInputStyle = {
        width: 180,
        borderRadius: "9999px",
        fontSize: "13px",
    };
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div style={{ margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <span style={{ cursor: "pointer", color: "#3b3b3bff" }} onClick={handleCancel}>
                            CV generator
                        </span>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Editing Details</Breadcrumb.Item>
                </Breadcrumb>
            </div>

            {/* Background Section */}
            <div style={{ marginBottom: 32 }}>
                <Title level={5}>Background and Relevant Experience</Title>
                <TextArea
                    value={editData.background}
                    onChange={(e) => setEditData({ ...editData, background: e.target.value })}
                    rows={4}
                />
            </div>

            {/* ─── Area of Expertise ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: 32 }}>
                <Title level={5}>Area of Expertise</Title>

                {/* Selected tags + free-text input */}
                <div style={tagInputContainerStyle}>
                    {editData.expertise.map((item, idx) => (
                        <Tag
                            key={idx}
                            closable
                            onClose={() => toggleExpertise(item)}
                            style={{
                                backgroundColor: "#F3F4F6",
                                color: "#111827",
                                border: "none",
                                borderRadius: "9999px",
                                padding: "4px 14px",
                                fontSize: "13px",
                            }}
                        >
                            {item}
                        </Tag>
                    ))}
                    <Input
                        size="small"
                        placeholder="Type and press Enter"
                        value={newExpertise}
                        onChange={(e) => setNewExpertise(e.target.value)}
                        onKeyDown={handleExpertiseKeyDown}
                        suffix={
                            <PlusOutlined
                                style={{ cursor: "pointer", color: "#52c41a" }}
                                onClick={handleAddExpertise}
                            />
                        }
                        style={addInputStyle}
                    />
                </div>

                {/* Suggestions */}
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">Suggested</Text>
                </div>
                <Space wrap style={{ padding: "10px", border: "1px solid #d5d5d5", borderRadius: "2px" }}>
                    {expertiseSuggestions.map((item, idx) => (
                        <Tag
                            key={idx}
                            onClick={() => toggleExpertise(item)}
                            style={{
                                padding: '4px 12px',
                                cursor: 'pointer',
                                borderRadius: "9999px",
                                background: editData.expertise.includes(item) ? '#C2FAE5' : '#fff',
                                borderColor: editData.expertise.includes(item) ? '#C2FAE5' : '#d9d9d9',
                                transition: 'all 0.3s',
                                fontSize: "13px",
                            }}
                        >
                            {item}
                        </Tag>
                    ))}
                </Space>
            </div>

            {/* ─── Languages ─────────────────────────────────────────────────────── */}
            <div style={{ marginBottom: 32 }}>
                <Title level={5}>Languages (Written & Spoken)</Title>

                {/* Selected tags + free-text input */}
                <div style={tagInputContainerStyle}>
                    {editData.languages.map((lang, idx) => (
                        <Tag
                            key={idx}
                            closable
                            onClose={() => toggleLanguage(lang)}
                            style={{
                                padding: '4px 16px',
                                borderRadius: "9999px",
                                fontSize: "13px",
                            }}
                        >
                            {lang}
                        </Tag>
                    ))}
                    <Input
                        size="small"
                        placeholder="Type and press Enter"
                        value={newLanguage}
                        onChange={(e) => setNewLanguage(e.target.value)}
                        onKeyDown={handleLanguageKeyDown}
                        suffix={
                            <PlusOutlined
                                style={{ cursor: "pointer", color: "#52c41a" }}
                                onClick={handleAddLanguage}
                            />
                        }
                        style={addInputStyle}
                    />
                </div>

                {/* Suggestions */}
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">Suggested</Text>
                </div>
                <Space wrap style={{ padding: "10px", border: "1px solid #d5d5d5", borderRadius: "2px" }}>
                    {languageSuggestions.map((lang, idx) => (
                        <Tag
                            key={idx}
                            onClick={() => toggleLanguage(lang)}
                            style={{
                                padding: '4px 12px',
                                cursor: 'pointer',
                                borderRadius: "9999px",
                                fontSize: "13px",
                                background: editData.languages.includes(lang) ? '#C2FAE5' : '#fff',
                                borderColor: editData.languages.includes(lang) ? '#C2FAE5' : '#d9d9d9',
                                transition: 'all 0.3s'
                            }}
                        >
                            {lang}
                        </Tag>
                    ))}
                </Space>
            </div>

            {/* ─── Education Details ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: 32 }}>
                <Title level={5}>Education Details</Title>

                {editData.education.map((edu, index) => (
                    <div
                        key={index}
                        style={{
                            marginBottom: 24,
                            padding: 20,
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            background: "#fafafa"
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <Title level={5} style={{ margin: 0 }}>Education {index + 1}</Title>
                            {editData.education.length > 1 && (
                                <DeleteOutlined
                                    onClick={() => removeEducation(index)}
                                    style={{ color: "#ff4d4f", fontSize: "18px", cursor: "pointer" }}
                                />
                            )}
                        </div>

                        <Row gutter={16}>
                            <Col span={12}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">Title of Education</Text>
                                    <Input
                                        value={editData.education[index].title}
                                        onChange={(e) => {
                                            const newEdu = [...editData.education];
                                            newEdu[index].title = e.target.value;
                                            setEditData({ ...editData, education: newEdu });
                                        }}
                                        style={{ marginTop: 8 }}
                                    />
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">Institute name</Text>
                                    <Input
                                        value={editData.education[index].institute}
                                        onChange={(e) => {
                                            const newEdu = [...editData.education];
                                            newEdu[index].institute = e.target.value;
                                            setEditData({ ...editData, education: newEdu });
                                        }}
                                        style={{ marginTop: 8 }}
                                    />
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">From</Text>
                                    <DatePicker
                                        inputReadOnly
                                        style={{ width: "100%", marginTop: 8 }}
                                        format="DD-MM-YYYY"
                                        value={editData.education[index].from ? dayjs(editData.education[index].from, "YYYY-MM-DD") : null}
                                        onChange={(date) => {
                                            const newEdu = [...editData.education];
                                            newEdu[index].from = date ? date.format("YYYY-MM-DD") : "";
                                            setEditData({ ...editData, education: newEdu });
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">To</Text>
                                    <DatePicker
                                        style={{ width: "100%", marginTop: 8 }}
                                        format="DD-MM-YYYY"
                                        value={editData.education[index].to ? dayjs(editData.education[index].to, "YYYY-MM-DD") : null}
                                        onChange={(date) => {
                                            const newEdu = [...editData.education];
                                            newEdu[index].to = date ? date.format("YYYY-MM-DD") : "";
                                            setEditData({ ...editData, education: newEdu });
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">Exam Body</Text>
                                    <Input
                                        value={editData.education[index].examBody}
                                        onChange={(e) => {
                                            const newEdu = [...editData.education];
                                            newEdu[index].examBody = e.target.value;
                                            setEditData({ ...editData, education: newEdu });
                                        }}
                                        style={{ marginTop: 8 }}
                                    />
                                </div>
                            </Col>

                            {/* ── Awarded — now a dropdown using AwardList ── */}
                            <Col span={12}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">Awarded</Text>
                                    <Select
                                        value={editData.education[index].awarded || undefined}
                                        onChange={(value) => {
                                            const newEdu = [...editData.education];
                                            newEdu[index].awarded = value;
                                            setEditData({ ...editData, education: newEdu });
                                        }}
                                        placeholder="Select award"
                                        style={{ width: "100%", marginTop: 8 }}
                                    >
                                        <Option value="Doctorate">Doctorate</Option>
                                        <Option value="Master">Master</Option>
                                        <Option value="Bachelor">Bachelor</Option>
                                        <Option value="Diploma">Diploma</Option>
                                        <Option value="Certificate">Certificate</Option>
                                    </Select>
                                </div>
                            </Col>
                        </Row>

                        <div style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>Attachment</Text>
                            <Upload
                                fileList={Array.isArray(educationFiles[index]) ? educationFiles[index] : []}
                                maxCount={1}
                                accept=".pdf,"
                                beforeUpload={async (file) => {
                                    const id = generateUniqueId();
                                    const updatedFile = { uid: id, name: file.name, type: file.type, originFileObj: file };
                                    await uploadFile(updatedFile);
                                    setEducationFiles((prev) => ({ ...prev, [index]: [updatedFile] }));
                                    return false;
                                }}
                                onRemove={() => {
                                    setEducationFiles((prev) => ({ ...prev, [index]: [] }));
                                }}
                            >
                                <Button
                                    block
                                    icon={<UploadOutlined />}
                                    style={{ height: 40, background: "#C2FAE5", borderColor: "#C2FAE5", borderRadius: "5px" }}
                                >
                                    Upload File
                                </Button>
                            </Upload>
                        </div>
                    </div>
                ))}

                <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined style={{ fontSize: 12 }} />}
                    onClick={addEducation}
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                    Add another
                </Button>
            </div>

            <Divider />

            {/* ─── Professional Certification ────────────────────────────────────── */}
            <div style={{ marginBottom: 32 }}>
                <Title level={5}>Professional Certification</Title>

                {editData.certifications.map((cert, index) => (
                    <div
                        key={index}
                        style={{
                            marginBottom: 24,
                            padding: 20,
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            background: "#fafafa"
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <Title level={5} style={{ margin: 0 }}>Certification {index + 1}</Title>
                            {editData.certifications.length > 1 && (
                                <DeleteOutlined
                                    onClick={() => removeCertification(index)}
                                    style={{ color: "#ff4d4f", fontSize: "18px", cursor: "pointer" }}
                                />
                            )}
                        </div>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Text type="secondary">Certification Title</Text>
                                <Input
                                    value={cert.title}
                                    onChange={(e) => {
                                        const updated = [...editData.certifications];
                                        updated[index].title = e.target.value;
                                        setEditData({ ...editData, certifications: updated });
                                    }}
                                    style={{ marginTop: 8 }}
                                />
                            </Col>

                            <Col span={12}>
                                <Text type="secondary">Certification Type</Text>
                                <Select
                                    value={cert.type}
                                    onChange={(value) => {
                                        const updated = [...editData.certifications];
                                        updated[index].type = value;
                                        setEditData({ ...editData, certifications: updated });
                                    }}
                                    style={{ marginTop: 8, width: "100%" }}
                                    placeholder="Select Certification Type"
                                >
                                    {AwardList?.map((item, i) => (
                                        <Option key={i} value={item}>{item}</Option>
                                    ))}
                                </Select>
                            </Col>

                            <Col span={12} style={{ marginTop: 16 }}>
                                <Text type="secondary">From</Text>
                                <DatePicker
                                    style={{ width: "100%", marginTop: 8 }}
                                    value={cert.from ? dayjs(cert.from, "YYYY-MM-DD") : null}
                                    format="DD-MM-YYYY"
                                    onChange={(date) => {
                                        const updated = [...editData.certifications];
                                        updated[index].from = date ? date.format("YYYY-MM-DD") : "";
                                        setEditData({ ...editData, certifications: updated });
                                    }}
                                />
                            </Col>

                            <Col span={12} style={{ marginTop: 16 }}>
                                <Text type="secondary">To</Text>
                                <DatePicker
                                    style={{ width: "100%", marginTop: 8 }}
                                    value={cert.to ? dayjs(cert.to, "YYYY-MM-DD") : null}
                                    format="DD-MM-YYYY"
                                    onChange={(date) => {
                                        const updated = [...editData.certifications];
                                        updated[index].to = date ? date.format("YYYY-MM-DD") : "";
                                        setEditData({ ...editData, certifications: updated });
                                    }}
                                />
                            </Col>
                        </Row>

                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary">Remark</Text>
                            <Input
                                value={cert.remark}
                                onChange={(e) => {
                                    const updated = [...editData.certifications];
                                    updated[index].remark = e.target.value;
                                    setEditData({ ...editData, certifications: updated });
                                }}
                                style={{ marginTop: 8 }}
                            />
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>Attachment</Text>
                            <Upload
                                fileList={Array.isArray(certificationFiles?.[index]) ? certificationFiles[index] : []}
                                maxCount={1}
                                accept=".pdf,.png,.jpg,.jpeg"
                                beforeUpload={async (file) => {
                                    const id = generateUniqueId();
                                    const newFile = { uid: id, name: file.name, type: file.type, status: "done", originFileObj: file };
                                    await uploadFile(newFile);
                                    setCertificationFiles((prev) => ({ ...prev, [index]: [newFile] }));
                                    return false;
                                }}
                                onRemove={() => {
                                    setCertificationFiles((prev) => ({ ...prev, [index]: [] }));
                                }}
                            >
                                <Button
                                    block
                                    icon={<UploadOutlined />}
                                    style={{ height: 40, background: "#C2FAE5", borderColor: "#C2FAE5", borderRadius: "5px" }}
                                >
                                    Upload File
                                </Button>
                            </Upload>
                        </div>
                    </div>
                ))}

                <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined style={{ fontSize: 12 }} />}
                    onClick={addCertification}
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                    Add another
                </Button>
            </div>

            {/* Action Buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 20,
                marginTop: 32,
                position: 'sticky',
                bottom: 0,
                background: '#fff',
                padding: '16px 0',
                borderTop: '1px solid #f0f0f0'
            }}>
                <Button size="large" onClick={handleCancel} style={{ width: '150px', borderRadius: '10px', borderColor: '#d9d9d9' }}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    size="large"
                    loading={updateLoading}
                    onClick={handleUpdate}
                    style={{ background: '#52c41a', borderColor: '#52c41a', width: '150px', borderRadius: '10px' }}
                >
                    Update
                </Button>
            </div>
        </div>
    );
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators({ updateUserList: setUserList }, dispatch);

const mapStateToProps = (state) => ({
    userRoleId: getUserRoleId(state),
    statusListFromID: getStatusListFromID(state, ["4", "5", "6"]),
    selectedDepartmentId: getSelectedDepartmentId(state),
    userInfo: getUserInfo(state),
    leaveCategoryList: getLeaveCategoryList(state),
    ClientId: getClientid(state),
    userList: getUserList(state),
    roleList: getRoleList(state),
});

export default connect(mapStateToProps, mapDispatchToProps, null)(CVEditor);